import Layout from '@layout/index';
import { SWRConfig } from 'swr';
import { fetchGoalInfo } from '@api/fetchers/goal';
import PageReduxProvider from '@cont/App/PageReduxProviderWrapper';
import { getKey as getTopologyKey } from '@api/hooks/topology/useTopology';
import styled from '@emotion/styled';
import Paper from '@base/Paper/Paper';
import { mediaQuery, Spacings } from '@styles/index';
import Typography from 'aquilla/core/atoms/Typography';
import { goBack } from '@utils/breadcrumbs';
import BreadCrumbs from '@base/BreadCrumbs/BreadCrumbs';
import {
  getBreadcrumbs,
  getGoalCuratedPlaylistUrl
} from '@cont/CuratedClasses/utils';
import { pageMetaInfo } from '@cont/CuratedClasses/constants';
import CuratedPlaylistCards from '@cont/CuratedClasses/CuratedPlaylistCards';
import resolvePromiseToObject from '@utils/resolvePromiseToObj';
import { fetchEducatorCuratedPlaylist } from '@api/fetchers/curated-playlist';
import { useSetSelectedNav } from '@stores/sidenav';
import plusNavBarItems from '@layout/NavBar/PlusNavBar/plusNavBarItems';
import Head from 'next/head';
import SEO from '@seo/index';

const StyledPaper = styled(Paper)`
  display: flex;
  flex-direction: column;
  align-items: baseline;
  margin-bottom: ${Spacings.SPACING_8B};
  padding: ${Spacings.SPACING_9B} ${Spacings.SPACING_12B}
    ${Spacings.SPACING_10B};
  background: var(--color-base-1);

  ${mediaQuery.uptoMobile} {
    background: var(--color-base-0);
    padding: ${Spacings.SPACING_6B}${Spacings.SPACING_0B} ${Spacings.SPACING_0B};
    box-shadow: none;
    flex-direction: column;
    align-items: flex-start;
  }
`;

const Title = styled(Typography)`
  margin-top: ${Spacings.SPACING_6B};
`;

const Description = styled(Typography)`
  margin-top: ${Spacings.SPACING_3B};
`;

const CuratedPlaylistContainer = ({ goal, url }) => {
  return (
    <>
      <StyledPaper
        gridColumn="span 18"
        padding={`${Spacings.SPACING_10B} ${Spacings.SPACING_12B}`}
      >
        <BreadCrumbs
          onBackClick={goBack}
          tags={getBreadcrumbs(goal)}
          useBackClick
        />
        <Title as="h1" variant="h3">
          {pageMetaInfo.title}
        </Title>
        <Description variant="p2" color="var(--color-text-secondary)">
          {pageMetaInfo.description}
        </Description>
      </StyledPaper>
      <CuratedPlaylistCards url={url} cardsPerRow={3} />
    </>
  );
};

const CuratedPlaylist = ({ goal, fallbackData, goalUID, querySlug }) => {
  const key = getGoalCuratedPlaylistUrl(goalUID);
  useSetSelectedNav(plusNavBarItems.FREE_CLASSES_AND_TESTS);
  const goalSlug = goal?.slug || querySlug;
  const canonicalURL = `https://unacademy.com/goal/${goalSlug}/${goalUID}/curated-classes`;
  const metaTitle = `${goal?.name} Curated Classes for Structured Learning`;
  const metaDescription = `Explore curated classes for ${goal?.name} with structured learning paths, syllabus aligned courses, and guided preparation for exam readiness.`;
  return (
    <>
      <SEO
        title={metaTitle}
        description={metaDescription}
        url={canonicalURL}
        openGraph={{
          title: metaTitle,
          description: metaDescription
        }}
      />
      <Head>
        <title>{metaTitle}</title>
      </Head>
      <SWRConfig
        value={{
          revalidateOnMount: false,
          dedupingInterval: 60 * 1000,
          fallback: {
            [key]: fallbackData.curatedPlaylistData
          }
        }}
      >
        <CuratedPlaylistContainer goal={goal} url={key} />
      </SWRConfig>
    </>
  );
};

CuratedPlaylist.pageInfo = {
  isMWebSupported: true,
  name: 'Curated Playlist',
  layoutOptions: {
    showPlusNav: true
  }
};

export default CuratedPlaylist;

export const getStaticProps = async ({ params: query }) => {
  const { goalUID: routeGoalUID = '', goalSlug: querySlug = '-' } = query;
  try {
    const goalUID = routeGoalUID.toUpperCase();
    const [goal] = await Promise.all([fetchGoalInfo(goalUID)]);
    const promises = [];
    const url = getGoalCuratedPlaylistUrl(goalUID);
    promises.push(
      resolvePromiseToObject(
        fetchEducatorCuratedPlaylist(url),
        'curatedPlaylistData'
      )
    );
    const fallbackData = await Promise.all(promises).then((resolvedValues) =>
      resolvedValues.reduce(
        (acc, next) => ({ ...acc, [next.type]: next.result }),
        {}
      )
    );
    return {
      props: {
        goalUID,
        querySlug,
        goal,
        fallbackData,
        globalFallbackData: {
          [getTopologyKey(goal.uid)]: goal
        }
      },
      revalidate: 5 * 60
    };
  } catch (error) {
    return { notFound: true, revalidate: 60 };
  }
};

export const getStaticPaths = async () => {
  return {
    paths: [],
    fallback: 'blocking'
  };
};

CuratedPlaylist.Layout = Layout;
CuratedPlaylist.Wrapper = PageReduxProvider;
